import React from 'react'
import { RotateCcw, ShieldCheck } from 'lucide-react'

import { CONFIG_MODE_OPTIONS } from '../../console-constants'
import type { InlineResult, OutboundResponseMode } from '../../console-types'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { ControlledSelectField, Field, ResultBlock, fieldsetClass, legendClass } from './shared'

export function AdminTab({
  configMode,
  setConfigMode,
  configRejectCode,
  setConfigRejectCode,
  isSavingConfig,
  configResult,
  onSaveConfig,
  isResetting,
  resetResult,
  onResetRuntime,
}: {
  configMode: OutboundResponseMode
  setConfigMode: (value: OutboundResponseMode) => void
  configRejectCode: string
  setConfigRejectCode: (value: string) => void
  isSavingConfig: boolean
  configResult: InlineResult | null
  onSaveConfig: (event: React.FormEvent<HTMLFormElement>) => void
  isResetting: boolean
  resetResult: InlineResult | null
  onResetRuntime: () => void
}) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl font-bold">Admin</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure o comportamento do mock e gerencie o estado em memória.
        </p>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        {/* Config card */}
        <div className="space-y-4">
          <fieldset className={fieldsetClass}>
            <legend className={legendClass}>Resposta Outbound pacs.008</legend>
            <p className="mb-4 text-xs text-muted-foreground">
              Configura como o mock responde quando o participante envia uma pacs.008 via{' '}
              <code className="rounded border border-border/50 bg-muted px-1.5 py-0.5 font-mono text-[11px]">
                /v3/icom/pacs/008
              </code>
              . Afeta as próximas transações outbound.
            </p>
            <form onSubmit={onSaveConfig} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Modo de Resposta">
                  <ControlledSelectField
                    value={configMode}
                    onValueChange={(value) => setConfigMode(value as OutboundResponseMode)}
                    placeholder="Modo de Resposta"
                    options={CONFIG_MODE_OPTIONS}
                  />
                </Field>
                {configMode === 'RJCT' ? (
                  <Field label="Código de Rejeição">
                    <Input
                      value={configRejectCode}
                      onChange={(event) => setConfigRejectCode(event.target.value)}
                      placeholder="AG03"
                      className="bg-input font-mono text-sm"
                    />
                  </Field>
                ) : (
                  <div />
                )}
              </div>
              <Button type="submit" disabled={isSavingConfig} className="gap-2">
                <ShieldCheck className="size-4" />
                {isSavingConfig ? 'Salvando...' : 'Salvar Configuração'}
              </Button>
            </form>
          </fieldset>

          {configResult && <ResultBlock result={configResult} />}
        </div>

        {/* Reset card */}
        <div className="space-y-4">
          <fieldset className={fieldsetClass}>
            <legend className={legendClass}>Reset Runtime</legend>
            <p className="mb-4 text-xs text-muted-foreground">
              Limpa as requests registradas e reseta o estado em memória do mock, incluindo
              transações, mensagens e long poll.
            </p>

            <div className="mb-4 rounded-md border border-amber-500/20 bg-amber-500/8 px-4 py-3 text-xs leading-5 text-amber-300/80">
              Replay e reload de seeds foram removidos da console para deixar o fluxo mais enxuto.
            </div>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="gap-2">
                  <RotateCcw className="size-4" />
                  Reset
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="border-border bg-card">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display font-bold">
                    Resetar todo o estado do mock?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-muted-foreground">
                    Essa ação limpa as requests registradas e reseta os dados em memória do mock.
                    Não é possível desfazer.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onResetRuntime}
                    disabled={isResetting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isResetting ? 'Resetando...' : 'Confirmar reset'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </fieldset>

          {resetResult && <ResultBlock result={resetResult} />}
        </div>
      </div>
    </div>
  )
}
